import classNames from 'classnames'
import {useRef, useEffect, useCallback, useMemo, memo} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import EntityPath from '../../../../../../containers/EntityPath'
import {useBranch} from '../../../../../../hooks/routes'
import {useIsAutodetectTimeZone} from '../../../../../../hooks/useIsAutodetectTimeZone'
import {useHeightResizeObserver} from '../../../../../../hooks/useResizeObserver'
import {getHrefWithQueryParams} from '../../../../../../routes'
import type {ProjectId, TestOccurrenceId} from '../../../../../../types'
import {getProjectBuildTypeFilter, stringifyId} from '../../../../../../types'
import {displayDate, formatDurationWithMilliseconds} from '../../../../../../utils/dateTimeUtils'
import {scrollIntoViewIfNeeded} from '../../../../../../utils/dom'
import {noop} from '../../../../../../utils/empty'
import BuildAgentLink from '../../../../../common/AgentLink/BuildAgentLink.container'
import BuildBranch from '../../../../../common/BranchLabel/BuildBranchLabel.container'
import BuildNumberContainer from '../../../../../common/BuildNumber/BuildNumber.container'
import BuildStatus from '../../../../../common/BuildStatus/BuildStatus.container'
import Details from '../../../../../common/Details/Details'
import RowGroup from '../../../../../common/Grid/RowGroup'
import IconButton from '../../../../../common/IconButton/IconButton'
import SvgIcon from '../../../../../common/SvgIcon/SvgIcon'
import {getFullBuildLogLinkParams} from '../../../../../packages/BuildLog/BuildLog.utils'
import BuildChangesDropdown from '../../../../../packages/Changes/ChangesDropdown/BuildChangesDropdown.container'
import BuildTestTab from '../../../../../packages/Tests/TestDetails/BuildTestTab/BuildTestTab.container'
import {getTestOccurrence} from '../../../../../packages/Tests/Tests.selectors'
import TestStatus from '../../../../../packages/Tests/TestStatus/TestStatus'

import styles from './TestHistoryItem.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  className?: string
  testOccurrenceId?: TestOccurrenceId
  projectId: ProjectId
  expanded?: boolean
  onExpandedTest?: (id: TestOccurrenceId) => unknown
}

function TestHistoryItem({
  testOccurrenceId,
  projectId,
  expanded = false,
  onExpandedTest = noop,
}: Props) {
  const branch = useBranch()
  const location = useLocation()
  const navigate = useNavigate()
  const ref = useRef<HTMLDivElement>(null)
  const [innerHeight, measureRef] = useHeightResizeObserver()

  const isAutodetectTimeZone = useIsAutodetectTimeZone()
  const testOccurrence = useAppSelector(state => getTestOccurrence(state, testOccurrenceId))
  const {name, duration, build, logAnchor} = testOccurrence ?? {}
  const {id: testBuildId, buildTypeId: testBuildTypeId, startDate} = build ?? {}

  useEffect(() => {
    if (expanded && ref.current != null) {
      scrollIntoViewIfNeeded(ref.current)
    }
  })

  const onToggle = useCallback(
    () => testOccurrenceId != null && onExpandedTest(testOccurrenceId),
    [testOccurrenceId, onExpandedTest],
  )

  const showBuildLog = useCallback(
    () =>
      testBuildId != null
        ? navigate(
            getHrefWithQueryParams(location, null, params => ({
              ...params,
              ...getFullBuildLogLinkParams({
                buildId: testBuildId,
                line: logAnchor,
                expandState: [Number(logAnchor)],
                logFilter: 'debug',
              }),
            })),
          )
        : null,
    [testBuildId, location, logAnchor, navigate],
  )

  const renderRow = useMemo(
    () =>
      testBuildId != null ? (
        <>
          <div className={styles.row}>
            <div className={styles.statusColumn}>
              <TestStatus testOccurrenceId={testOccurrenceId} withCounters />
            </div>
            <div className={styles.durationColumn}>{`${formatDurationWithMilliseconds(
              duration || 0,
            )}`}</div>
            <div className={styles.buildColumn}>
              <BuildNumberContainer className={styles.buildNumber} buildId={testBuildId} hideStar />
            </div>
            <div className={styles.buildDetailsColumn}>
              <div>
                {!branch && (
                  <BuildBranch className={styles.buildBranch} buildId={testBuildId} defaultTrim />
                )}
                <EntityPath
                  className={styles.buildPath}
                  buildTypeId={testBuildTypeId}
                  projectOrBuildTypeNode={getProjectBuildTypeFilter({projectId})}
                  withLeafStatusIcon={false}
                  withCollapsing
                />
              </div>
              <BuildStatus className={styles.buildStatus} buildId={testBuildId} />
            </div>
            <div className={styles.changesColumn}>
              <BuildChangesDropdown buildId={testBuildId} />
              <BuildAgentLink className={styles.buildAgentLink} buildId={testBuildId} />
            </div>
            <div className={styles.agentColumn}>
              <BuildAgentLink
                className={styles.buildAgentLink}
                buildId={testBuildId}
                secondary={false}
              />
            </div>
            <div className={styles.buildStartDateColumn}>
              {startDate != null && displayDate(startDate, isAutodetectTimeZone, true)}
            </div>
            <div className={styles.actionsColumn}>
              {logAnchor != null && (
                <IconButton
                  className={styles.showInBuildLogIcon}
                  icon="log"
                  title="Show in Build Log"
                  onClick={showBuildLog}
                />
              )}
            </div>
          </div>
          <SvgIcon className={styles.arrow} icon={expanded ? 'chevron-up' : 'chevron-down'} />
        </>
      ) : null,
    [
      testOccurrenceId,
      duration,
      testBuildId,
      branch,
      testBuildTypeId,
      projectId,
      startDate,
      isAutodetectTimeZone,
      logAnchor,
      showBuildLog,
      expanded,
    ],
  )

  const getElementId: () => string = () => `test-occurrence-${stringifyId(testOccurrenceId)}`

  return (
    <RowGroup span={2}>
      <div
        id={getElementId()}
        ref={ref}
        className={classNames(styles.item, {
          [styles.open]: expanded,
          [styles.closed]: !expanded,
        })}
      >
        <Details
          headingClassName={styles.heading}
          level={2}
          label={name ?? 'Test'}
          open={expanded}
          summary={renderRow}
          summaryClassName={styles.button}
          detailsWrapperClassName={styles.detailsWrapper}
          onToggle={onToggle}
          imitateHeading
          renderButtonAside
        >
          <div style={{height: innerHeight}} className={styles.outerContainer}>
            <div ref={measureRef} className={styles.innerContainer}>
              <BuildTestTab
                className={styles.details}
                buildId={testBuildId}
                testOccurrenceId={testOccurrenceId}
              />
            </div>
          </div>
        </Details>
      </div>
    </RowGroup>
  )
}

export default memo<Props>(TestHistoryItem)
